import { Image, buildSrc } from "@imagekit/react";
import { useState } from "react";
import defaultProductImage from "../../assets/images/default-product.png";

// Single source of truth for the ImageKit endpoint (also consumed by <ImageKitProvider> in index.js).
// Trailing slash stripped so the SDK doesn't build "//" when it joins urlEndpoint + "/path".
// The default keeps images working even when a build fails to inject the env var — the dev deploy
// sets REACT_APP_ENV but not REACT_APP_ImageKit_URL (added later, during the SDK migration), so an
// empty endpoint made every image 404 on dev while localhost worked. The URL is a public CDN
// endpoint, identical across all env files, so hardcoding it as a fallback is safe.
export const IK_ENDPOINT = (
  process.env.REACT_APP_ImageKit_URL || "https://ik.imagekit.io/3quk5czhx/"
).replace(/\/$/, "");

// Resolve any image reference to an ImageKit-relative "/path" the SDK can serve, or null to fall
// back to a plain <img>. The ImageKit endpoint is mapped to the storage bucket's /public/ root, so:
//   - S3 bucket URLs (…/public/temp/x.jpg)      -> "/temp/x.jpg"  (served + optimized via ImageKit)
//   - existing ImageKit URLs                     -> normalized "/path" (drop the imagekit_id)
//   - genuinely external hosts / bundled assets  -> null (defaultProductImage → /static, data:, blob:)
//   - already-relative pre-sliced paths          -> "/folder/file"
export const ikPath = (v) => {
  if (!v) return null;
  const p = v.split("?")[0];
  let path;
  if (p.includes("/public/")) path = "/" + p.split("/public/")[1];
  else if (p.includes("ik.imagekit.io/"))
    path = "/" + p.split("ik.imagekit.io/")[1].split("/").slice(1).join("/");
  else if (/^(https?:|data:|blob:|\/static\/|\/assets\/)/.test(p)) return null;
  else path = "/" + p.replace(/^\/+/, "");
  // Backend filenames arrive fully percent-encoded (e.g. "Kurti_1%20%28Virtual%20Model%29.webp",
  // "ChatGPT Image Sep 8, 2026.png" -> "...Sep%208%2C%202026.png"). ImageKit resolves an object by
  // its RAW key, so it 404s on %28/%29 (parens) and %2C (commas) alike. Decode the whole path once
  // instead of un-escaping one character class at a time: buildSrc/encodeURI re-encodes only what
  // must be encoded (a raw space becomes %20 again; parens and commas stay raw), so this yields the
  // exact URL ImageKit serves. A stray literal "%" in a filename makes decodeURIComponent throw —
  // keep the original path in that case rather than dropping the image.
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
};

// Full optimized ImageKit URL as a plain string — for contexts that can't use the <Image> component
// (e.g. a <video poster> attribute). Non-CDN sources fall through to the original value.
export const ikSrc = (v, transformation) => {
  const path = ikPath(v);
  return path ? buildSrc({ urlEndpoint: IK_ENDPOINT, src: path, transformation }) : v || "";
};

// Product media is sometimes a video (an mp4 uploaded as the product image). In an image
// context (cards, thumbnails) we serve a poster FRAME from ImageKit — ".../file.mp4/ik-thumbnail.jpg"
// — so it's CDN-optimized and the image-only blur transform doesn't 400 on a video.
const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogv|ogg)$/i;

/**
 * THE single definition of "this URL is a video", exported so no caller has to re-derive
 * it. Previously private to this module; promoted because the PDP needs the same answer
 * and two regexes that drift apart would render an <img src="....mp4"> — a blank tile.
 *
 * Needed because `prd_gal_media_type` cannot be trusted: on dev, 5 active gallery rows
 * carry a video extension but are NOT tagged `video/*`, 49 rows are NULL, and staging
 * uses the non-MIME value "Image". So the extension is the reliable signal and the
 * column is the hint, not the reverse.
 *
 * Query string stripped first: gallery URLs can arrive with `?tr=` or other params, and
 * `foo.mp4?x=1` must still test true.
 */
export const isVideoUrl = (v) =>
  Boolean(v) && VIDEO_EXT.test(String(v).split("?")[0]);

// ponytail: CSS-only blur-up. A tiny blurred backdrop shows until the full image paints over it.
// Correct for opaque, object-cover/overflow-hidden surfaces (all current callers). A transparent
// PNG or object-contain image would show the blur in the uncovered area — use OptimizedImage there.
const blurStyle = (path, extra) => ({
  backgroundImage: `url("${buildSrc({
    urlEndpoint: IK_ENDPOINT,
    src: path,
    transformation: [{ width: 40, quality: 20, blur: 30 }],
  })}")`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  ...extra,
});

// lqip: blur-up backdrop. Disable it for object-contain surfaces (the blur would bleed into the
// letterbox bars around a contained image).
// `eager` opts out of native loading="lazy". Native lazy-loading does not trigger for <img>
// inside a horizontally scroll-snapping (overflow-x:auto) carousel — even when the image is in
// the viewport — so those product rows show only the blur placeholder and never load. Callers
// that render inside such a carousel pass eager so the images load reliably.
export function Imagekit({
  IMGName,
  className = "",
  alt,
  sizes,
  width = 800,
  lqip = true,
  eager = false,
  ...rest
}) {
  const [errored, setErrored] = useState(false);
  if (!IMGName) return null;
  const path = ikPath(IMGName);
  const altText = alt || IMGName.split("/").pop();
  // No ImageKit path (external host / bundled asset): render the original URL as a plain <img>.
  if (!path) {
    return (
      <img
        src={IMGName}
        alt={altText}
        className={className}
        loading={eager ? "eager" : "lazy"}
        style={{ justifySelf: "center" }}
        {...rest}
      />
    );
  }
  // ImageKit fetch failed (file missing / 404 on the CDN): show the local placeholder rather than a
  // broken-image icon. Retrying the same dead URL would only break again.
  if (errored) {
    return (
      <img
        src={defaultProductImage}
        alt={altText}
        className={className}
        loading={eager ? "eager" : "lazy"}
        style={{ justifySelf: "center" }}
        {...rest}
      />
    );
  }
  // Video source in an image slot: render an ImageKit-generated poster frame (no blur — it 400s on video).
  if (isVideoUrl(path)) {
    const posterSrc = buildSrc({
      urlEndpoint: IK_ENDPOINT,
      src: path + "/ik-thumbnail.jpg",
      transformation: [{ width }],
    });
    return (
      <img
        src={posterSrc}
        alt={altText}
        className={className}
        loading={eager ? "eager" : "lazy"}
        onError={() => setErrored(true)}
        style={{ justifySelf: "center" }}
        {...rest}
      />
    );
  }
  return (
    <Image
      src={path}
      alt={altText}
      className={className}
      width={width}
      responsive
      sizes={sizes || "(max-width: 600px) 100vw, 500px"}
      loading={eager ? "eager" : "lazy"}
      onError={() => setErrored(true)}
      style={lqip ? blurStyle(path, { justifySelf: "center" }) : { justifySelf: "center" }}
      {...rest}
    />
  );
}

// These thumbnails live in horizontal scroll-snapping product carousels where native
// loading="lazy" never fires (see Imagekit above), so they load eagerly by default.
export function ImageKitThumbNail({ Thumbnail, className = "", alt, eager = true, ...rest }) {
  const [errored, setErrored] = useState(false);
  if (!Thumbnail) return null;
  const path = ikPath(Thumbnail);
  const altText = alt || Thumbnail.split("/").pop();
  if (!path) {
    return (
      <img
        src={Thumbnail}
        alt={altText}
        className={className}
        loading={eager ? "eager" : "lazy"}
        style={{ borderRadius: "10%" }}
        {...rest}
      />
    );
  }
  // CDN fetch failed: local placeholder instead of a broken-image icon.
  if (errored) {
    return (
      <img
        src={defaultProductImage}
        alt={altText}
        className={className}
        loading={eager ? "eager" : "lazy"}
        style={{ borderRadius: "10%" }}
        {...rest}
      />
    );
  }
  return (
    <Image
      src={path}
      alt={altText}
      className={className}
      // 220² (2x of ~110px) top-anchored square crop; fills the box, trimming
      // overflow from the BOTTOM so heads/subjects at the top stay visible.
      // ponytail: fixed size — bump if a caller renders these larger than ~110px CSS.
      transformation={[{ width: 220, height: 220, focus: "top" }]}
      width={90}
      height={90}
      responsive={false}
      loading={eager ? "eager" : "lazy"}
      onError={() => setErrored(true)}
      style={blurStyle(path, { borderRadius: "10%" })}
      {...rest}
    />
  );
}
